import React, {Component} from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { ColorRing } from 'react-loader-spinner';
import moment from "moment";
import { toast } from 'react-toastify';

import PageTitle from "../../../components/common/PageTitle";
import Chart from "../../../utils/chart";
import history from "../../../utils/history";
import {
  Button, ButtonGroup,
  Col, Row,
  Card, CardHeader, CardBody,
  FormSelect, FormInput
} from "shards-react";

// reviewList 기능 이벤트(eventName) → 한글 라벨/카테고리 (단일 출처 — 백엔드는 raw eventName 반환)
const REVIEW_FEATURE_LABELS = {
  'reviewList:search':       { label: '검색',                 category: '검색/필터' },
  'reviewList:filterStar':   { label: '별점 필터',            category: '검색/필터' },
  'reviewList:filterStatus': { label: '상태 필터(미작성/대기)', category: '검색/필터' },
  'reviewList:viewMode':     { label: '보기 모드 전환',        category: '검색/필터' },
  'reviewList:replySave':    { label: '답글 저장',             category: '답글/AI' },
  'reviewList:aiComment':    { label: 'AI 답글 생성',          category: '답글/AI' },
  'reviewList:smartCorrect': { label: 'AI 다듬기(스마트교정)',  category: '답글/AI' },
  'reviewList:fixComment':   { label: '고정 댓글 불러오기',     category: '답글/AI' },
  'reviewList:editContent':  { label: '리뷰 내용 수정',         category: '내용/이미지' },
  'reviewList:editWriterAt': { label: '작성일 수정',           category: '내용/이미지' },
  'reviewList:uploadImage':  { label: '이미지 업로드',          category: '내용/이미지' },
  'reviewList:changeStatus': { label: '게시 상태 변경',         category: '상태/노출' },
  'reviewList:togglePin':    { label: '상단 고정 토글',         category: '상태/노출' },
  'reviewList:bulkStatus':   { label: '일괄 상태 변경',         category: '일괄처리' },
  'reviewList:bulkPin':      { label: '일괄 상단 고정',         category: '일괄처리' },
  'reviewList:bulkDelete':   { label: '일괄 삭제',             category: '일괄처리' },
  'reviewList:deleteOne':    { label: '리뷰 삭제',             category: '삭제' },
  'reviewList:syncReviews':  { label: '리뷰 동기화',           category: '동기화' },
  'reviewList:exportCsv':    { label: 'CSV 내보내기',          category: '내보내기' },
  'reviewList:assignWidget': { label: '위젯 지정',             category: '위젯' },
};

@inject(({ adminLog }) => ({
  getAdminAccessStats: adminLog.getAdminAccessStats,
  getAdminAccessTopPages: adminLog.getAdminAccessTopPages,
  getAdminAccessTrend: adminLog.getAdminAccessTrend,
  getReviewListFeatureUsage: adminLog.getReviewListFeatureUsage,
  resetAdminAccessLogs: adminLog.resetAdminAccessLogs,
}))
@inject(({ AOA }) => ({ getStoreList: AOA.getStoreList }))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))

@observer
class AdminUsageDashboard extends Component {

  @observable isLoading = true;
  @observable period = '7d';   // 'today' | '7d' | '30d' | 'custom'
  @observable startDate = '';
  @observable endDate = '';
  @observable storeKeyword = '';
  @observable selectedStoreId = '';
  @observable storeOptions = [];
  @observable includeSuper = false;
  @observable stats = { totalPV: 0, uniqueUsers: 0, uniqueStores: 0, avgDurationMs: 0 };
  @observable topPages = [];
  @observable trend = [];
  @observable featureUsage = { events: [], adoption: {}, totalActiveStores: 0, totalActiveUsers: 0 }; // reviewList 기능 사용 분석
  @observable sortBy = 'pv';   // 'pv' | 'avgDurationMs'
  @observable isResetting = false;

  constructor(props) {
    super(props);
    this.trendChartRef = React.createRef();
    this.trendChartInstance = null;
  }

  componentDidMount() {
    // 권한 체크 — 슈퍼어드민만 (Sidebar.js:118 와 동일 패턴)
    const me = this.props.me || {};
    if (me.emailId !== 'admin@onnomads.com') {
      toast.error('접근 권한이 없습니다.');
      history.replace('/dashboard');
      return;
    }
    this.applyPeriod('7d');
    this.fetchStoreOptions();
    this.doSearch();
  }

  componentWillUnmount() {
    if (this.trendChartInstance) this.trendChartInstance.destroy();
  }

  // 기간 프리셋을 startDate/endDate로 변환
  @action applyPeriod = (p) => {
    this.period = p;
    const now = moment();
    if (p === 'today') {
      this.startDate = now.clone().startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
    } else if (p === '7d') {
      this.startDate = now.clone().subtract(6, 'days').startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
    } else if (p === '30d') {
      this.startDate = now.clone().subtract(29, 'days').startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
    }
    // custom 은 startDate/endDate 를 사용자가 직접 입력
  };

  @action setPeriod = (p) => {
    this.applyPeriod(p);
    if (p !== 'custom') this.doSearch();
  };

  @action setSortBy = (key) => {
    if (this.sortBy === key) return;
    this.sortBy = key;
    this.doSearch();
  };

  @action onChangeStore = (event) => {
    this.selectedStoreId = event.target.value;
    this.doSearch();
  };

  @action onChangeStoreKeyword = async (event) => {
    this.storeKeyword = event.target.value;
    this.fetchStoreOptions();
  };

  fetchStoreOptions = async () => {
    try {
      const params = {
        page: 1,
        limit: 50,
        searchParams: this.storeKeyword ? { keyword: this.storeKeyword } : {}
      };
      this.props.getStoreList(params, (res) => {
        if (res && res.docs) {
          this.storeOptions = res.docs.map(s => ({
            _id: s._id,
            name: s.name || s.domain || s._id
          }));
        }
      });
    } catch (e) { console.error('fetchStoreOptions', e); }
  };

  @action doSearch = async () => {
    this.isLoading = true;
    try {
      const params = {
        store_id: this.selectedStoreId || undefined,
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        includeSuper: this.includeSuper
      };

      const [statsRes, topRes, trendRes, featRes] = await Promise.all([
        new Promise(resolve => this.props.getAdminAccessStats(params, resolve)),
        new Promise(resolve => this.props.getAdminAccessTopPages({ ...params, limit: 30, sortBy: this.sortBy }, resolve)),
        new Promise(resolve => this.props.getAdminAccessTrend({ ...params, granularity: 'day' }, resolve)),
        new Promise(resolve => this.props.getReviewListFeatureUsage(params, resolve))
      ]);

      if (statsRes && statsRes.data) this.stats = statsRes.data;
      else this.stats = { totalPV: 0, uniqueUsers: 0, uniqueStores: 0, avgDurationMs: 0 };

      this.topPages = (topRes && topRes.data) || [];
      this.trend = (trendRes && trendRes.data) || [];
      this.featureUsage = (featRes && featRes.data) || { events: [], adoption: {}, totalActiveStores: 0, totalActiveUsers: 0 };

    } catch (e) {
      console.error(e);
      toast.error('데이터 조회 중 오류가 발생했습니다.');
    } finally {
      this.isLoading = false;
      setTimeout(() => this.renderTrendChart(), 100);
    }
  };

  @action resetData = (scope) => {
    // scope: 'range' (현재 필터 기간/스토어) | 'all' (전체)
    const isAll = scope === 'all';
    const storeName = this.selectedStoreId
      ? (this.storeOptions.find(s => s._id === this.selectedStoreId) || {}).name || this.selectedStoreId
      : '전체 스토어';

    const confirmMsg = isAll
      ? '⚠️ 전체 관리자 사용 분석 데이터를 영구 삭제합니다.\n이 작업은 되돌릴 수 없습니다.\n\n계속하시겠습니까?'
      : `⚠️ 다음 범위의 분석 데이터를 영구 삭제합니다.\n\n· 스토어: ${storeName}\n· 기간: ${this.startDate} ~ ${moment(this.endDate).subtract(1, 'day').format('YYYY-MM-DD')}\n\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`;

    if (!window.confirm(confirmMsg)) return;
    if (!window.confirm('정말 삭제하시겠습니까? 한 번 더 확인합니다.')) return;

    const params = isAll
      ? {}
      : {
          store_id: this.selectedStoreId || undefined,
          startDate: this.startDate || undefined,
          endDate: this.endDate || undefined
        };

    this.isResetting = true;
    this.props.resetAdminAccessLogs(params, (res) => {
      this.isResetting = false;
      if (res && res.ok) {
        toast.success(`데이터가 리셋되었습니다. (삭제: ${Number(res.deletedCount || 0).toLocaleString()}건)`);
        this.doSearch();
      } else {
        const reason = res && res.reason === 'forbidden' ? ' (권한 없음)' : '';
        toast.error('데이터 리셋에 실패했습니다.' + reason);
      }
    });
  };

  formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString();
  };

  formatDuration = (ms) => {
    if (!ms || ms <= 0) return '-';
    const totalSec = Math.round(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return `${min}분 ${sec}초`;
    return `${sec}초`;
  };

  renderTrendChart = () => {
    if (!this.trendChartRef.current) return;
    if (this.trendChartInstance) this.trendChartInstance.destroy();

    // 빈 구간 채우기 (시작-종료 사이 일별 라벨 만들기)
    const labels = [];
    const pvMap = {};
    const uvMap = {};
    (this.trend || []).forEach(d => {
      pvMap[d.bucket] = d.pv || 0;
      uvMap[d.bucket] = d.uv || 0;
    });

    if (this.startDate && this.endDate) {
      let cur = moment(this.startDate);
      const end = moment(this.endDate);
      while (cur.isBefore(end)) {
        labels.push(cur.format('YYYY-MM-DD'));
        cur.add(1, 'day');
      }
    } else {
      Object.keys(pvMap).sort().forEach(k => labels.push(k));
    }

    const pvData = labels.map(l => pvMap[l] || 0);
    const uvData = labels.map(l => uvMap[l] || 0);
    const displayLabels = labels.map(l => moment(l).format('MM/DD'));

    this.trendChartInstance = new Chart(this.trendChartRef.current, {
      type: 'LineWithLine',
      data: {
        labels: displayLabels,
        datasets: [
          {
            label: 'PV(페이지뷰)',
            fill: 'start',
            data: pvData,
            backgroundColor: 'rgba(90, 106, 240, 0.08)',
            borderColor: 'rgba(90, 106, 240, 1)',
            pointBackgroundColor: '#ffffff',
            pointHoverBackgroundColor: 'rgba(90, 106, 240, 1)',
            pointBorderColor: 'rgba(90, 106, 240, 0.6)',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 5,
            yAxisID: 'y-pv'
          },
          {
            label: 'UV(고유 사용자)',
            fill: 'start',
            data: uvData,
            backgroundColor: 'rgba(16, 185, 129, 0.08)',
            borderColor: 'rgba(16, 185, 129, 1)',
            pointBackgroundColor: '#ffffff',
            pointHoverBackgroundColor: 'rgba(16, 185, 129, 1)',
            pointBorderColor: 'rgba(16, 185, 129, 0.6)',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 5,
            yAxisID: 'y-uv'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'top', labels: { usePointStyle: true, padding: 20, fontSize: 13, fontColor: '#5a6169' } },
        elements: { line: { tension: 0.3 } },
        scales: {
          xAxes: [{
            gridLines: { display: true, color: 'rgba(0,0,0,0.04)' },
            ticks: { fontSize: 11, fontColor: '#aaa', maxRotation: 0, callback(t, i) { return i % 2 === 0 ? t : ''; } }
          }],
          yAxes: [
            {
              id: 'y-pv', position: 'left',
              gridLines: { display: true, color: 'rgba(0,0,0,0.04)' },
              ticks: { fontSize: 11, fontColor: 'rgba(90,106,240,0.8)', beginAtZero: true,
                       callback(t) { return t > 999 ? (t/1000).toFixed(1)+'K' : t; } },
              scaleLabel: { display: true, labelString: 'PV', fontColor: 'rgba(90,106,240,0.8)', fontSize: 12 }
            },
            {
              id: 'y-uv', position: 'right',
              gridLines: { display: false },
              ticks: { fontSize: 11, fontColor: 'rgba(16,185,129,0.8)', beginAtZero: true,
                       callback(t) { return t > 999 ? (t/1000).toFixed(1)+'K' : t; } },
              scaleLabel: { display: true, labelString: 'UV', fontColor: 'rgba(16,185,129,0.8)', fontSize: 12 }
            }
          ]
        },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index', intersect: false,
          backgroundColor: 'rgba(255,255,255,0.95)', titleFontColor: '#333', bodyFontColor: '#666',
          borderColor: '#e9ecef', borderWidth: 1, cornerRadius: 8, xPadding: 12, yPadding: 10,
          callbacks: { label(ti, data) { return ' ' + data.datasets[ti.datasetIndex].label + ': ' + Number(ti.yLabel || 0).toLocaleString(); } }
        }
      }
    });
  };

  renderKpiCard = (label, value, unit, color) => (
    <Col lg="3" md="6" sm="12" className="mb-3">
      <Card small>
        <CardBody style={{padding: '20px'}}>
          <div style={{fontSize: '13px', color: '#868e96', marginBottom: '6px'}}>{label}</div>
          <div style={{fontSize: '28px', fontWeight: '700', color: '#333'}}>
            {value}
            {unit && <span style={{fontSize: '14px', color: '#868e96', marginLeft: '4px'}}>{unit}</span>}
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  // 채택률 카드 (기능 사용 분석) — 좌측 컬러바 미사용, 텍스트 색만으로 강조
  renderAdoptionCard = (label, rate, hint, color) => (
    <Col lg="4" md="6" sm="12" className="mb-3">
      <Card small>
        <CardBody style={{padding: '18px 20px'}}>
          <div style={{fontSize: '13px', color: '#868e96', marginBottom: '6px'}}>{label}</div>
          <div style={{fontSize: '26px', fontWeight: '700', color: color}}>
            {(rate != null ? rate : 0)}<span style={{fontSize: '14px', marginLeft: '2px'}}>%</span>
          </div>
          <div style={{fontSize: '11px', color: '#adb5bd', marginTop: '4px'}}>{hint}</div>
        </CardBody>
      </Card>
    </Col>
  );

  render() {
    const me = this.props.me || {};
    if (me.emailId !== 'admin@onnomads.com') return null;

    const { totalPV, uniqueUsers, uniqueStores, avgDurationMs } = this.stats || {};

    return (
      <div style={{padding: '0px 16px 30px'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}} className="mb-4">
          <PageTitle title="관리자 사용 분석" subtitle="스토어 관리" className="mb-0"/>
          <ButtonGroup size="sm">
            <Button theme="warning"
                    disabled={this.isResetting}
                    onClick={() => this.resetData('range')}>
              {this.isResetting ? '삭제 중...' : '현재 범위 데이터 리셋'}
            </Button>
            <Button theme="danger"
                    disabled={this.isResetting}
                    onClick={() => this.resetData('all')}>
              전체 데이터 리셋
            </Button>
          </ButtonGroup>
        </div>

        {/* === 필터 바 === */}
        <Card className="mb-3">
          <CardBody style={{padding: '20px'}}>
            <Row>
              <Col md="6" className="mb-2">
                <div style={{fontSize: '12px', color: '#868e96', marginBottom: '6px'}}>기간</div>
                <ButtonGroup size="sm">
                  <Button theme={this.period === 'today' ? 'primary' : 'light'} onClick={() => this.setPeriod('today')}>오늘</Button>
                  <Button theme={this.period === '7d' ? 'primary' : 'light'} onClick={() => this.setPeriod('7d')}>최근 7일</Button>
                  <Button theme={this.period === '30d' ? 'primary' : 'light'} onClick={() => this.setPeriod('30d')}>최근 30일</Button>
                  <Button theme={this.period === 'custom' ? 'primary' : 'light'} onClick={() => this.setPeriod('custom')}>직접 입력</Button>
                </ButtonGroup>
                {this.period === 'custom' && (
                  <div style={{marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <FormInput
                      type="date"
                      size="sm"
                      value={this.startDate}
                      onChange={(e) => this.startDate = e.target.value}
                      style={{maxWidth: '160px'}}
                    />
                    <span>~</span>
                    <FormInput
                      type="date"
                      size="sm"
                      value={this.endDate ? moment(this.endDate).subtract(1, 'day').format('YYYY-MM-DD') : ''}
                      onChange={(e) => this.endDate = e.target.value ? moment(e.target.value).add(1, 'day').format('YYYY-MM-DD') : ''}
                      style={{maxWidth: '160px'}}
                    />
                    <Button size="sm" theme="primary" onClick={() => this.doSearch()}>조회</Button>
                  </div>
                )}
              </Col>
              <Col md="6" className="mb-2">
                <div style={{fontSize: '12px', color: '#868e96', marginBottom: '6px'}}>스토어 (전체 또는 특정 스토어)</div>
                <div style={{display: 'flex', gap: '8px'}}>
                  <FormInput
                    size="sm"
                    placeholder="스토어 검색 (이름/도메인)"
                    value={this.storeKeyword}
                    onChange={this.onChangeStoreKeyword}
                    style={{maxWidth: '220px'}}
                  />
                  <FormSelect size="sm" value={this.selectedStoreId} onChange={this.onChangeStore} style={{maxWidth: '320px'}}>
                    <option value="">전체 스토어</option>
                    {this.storeOptions.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </FormSelect>
                  <Button size="sm" theme="light" onClick={() => this.doSearch()}>새로고침</Button>
                </div>
                <div style={{marginTop: '10px', fontSize: '12px', color: '#868e96'}}>
                  <label style={{cursor: 'pointer', userSelect: 'none'}}>
                    <input
                      type="checkbox"
                      checked={this.includeSuper}
                      onChange={(e) => { this.includeSuper = e.target.checked; this.doSearch(); }}
                      style={{marginRight: '6px', verticalAlign: 'middle'}}
                    />
                    슈퍼어드민 본인 활동 포함 (디버깅)
                  </label>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* === KPI 카드 === */}
        <Row>
          {this.renderKpiCard('총 페이지뷰 (PV)', this.formatNumber(totalPV), '회', '#5a6af0')}
          {this.renderKpiCard('고유 사용자 (UV)', this.formatNumber(uniqueUsers), '명', '#10b981')}
          {this.renderKpiCard('활성 스토어', this.formatNumber(uniqueStores), '개', '#f59e0b')}
          {this.renderKpiCard('평균 체류 시간', this.formatDuration(avgDurationMs), '', '#8b5cf6')}
        </Row>

        {/* === 일별 트렌드 차트 === */}
        <Card className="mb-3">
          <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0'}}>
            <span style={{fontSize: '14px', fontWeight: '600'}}>일별 사용 추이</span>
          </CardHeader>
          <CardBody style={{padding: '16px 20px', height: '320px'}}>
            {this.isLoading ? (
              <div className="text-center" style={{paddingTop: '100px'}}>
                <ColorRing visible={true} height="50" width="50" colors={['#5a6169', '#5a6169', '#5a6169', '#5a6169', '#5a6169']}/>
              </div>
            ) : (
              <canvas ref={this.trendChartRef} style={{maxHeight: '290px'}}/>
            )}
          </CardBody>
        </Card>

        {/* === 페이지별 TOP 테이블 === */}
        <Card>
          <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: '14px', fontWeight: '600'}}>페이지별 사용 순위 (TOP 30)</span>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span style={{fontSize: '12px', color: '#868e96'}}>정렬:</span>
              <ButtonGroup size="sm">
                <Button theme={this.sortBy === 'pv' ? 'primary' : 'light'} onClick={() => this.setSortBy('pv')}>PV 순</Button>
                <Button theme={this.sortBy === 'avgDurationMs' ? 'primary' : 'light'} onClick={() => this.setSortBy('avgDurationMs')}>평균 체류 시간 순</Button>
              </ButtonGroup>
            </div>
          </CardHeader>
          <CardBody style={{padding: '0'}}>
            <table style={{width: '100%', fontSize: '13px'}}>
              <colgroup>
                <col width="5%" />
                <col width="20%" />
                <col />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
              </colgroup>
              <thead>
                <tr style={{backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef'}}>
                  <th style={{padding: '10px', textAlign: 'center'}}>순위</th>
                  <th style={{padding: '10px', textAlign: 'left'}}>페이지명</th>
                  <th style={{padding: '10px', textAlign: 'left'}}>경로</th>
                  <th style={{padding: '10px', textAlign: 'center'}}>메인 메뉴</th>
                  <th style={{padding: '10px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', color: this.sortBy === 'pv' ? '#5a6af0' : 'inherit'}}
                      onClick={() => this.setSortBy('pv')}>
                    PV {this.sortBy === 'pv' && '▼'}
                  </th>
                  <th style={{padding: '10px', textAlign: 'right'}}>UV (사용자)</th>
                  <th style={{padding: '10px', textAlign: 'right'}}>UV (스토어)</th>
                  <th style={{padding: '10px', textAlign: 'right', cursor: 'pointer', userSelect: 'none', color: this.sortBy === 'avgDurationMs' ? '#8b5cf6' : 'inherit'}}
                      onClick={() => this.setSortBy('avgDurationMs')}>
                    평균 체류 {this.sortBy === 'avgDurationMs' && '▼'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(this.topPages && this.topPages.length > 0) ? (
                  (() => {
                    // 평균 체류값이 0(또는 1초 미만)인 항목은 항상 하단으로 배치
                    const ZERO_THRESHOLD = 1000; // 1초 미만은 0 으로 간주
                    const list = (this.topPages || []).slice();
                    list.sort((a, b) => {
                      const aZero = !a.avgDurationMs || a.avgDurationMs < ZERO_THRESHOLD ? 1 : 0;
                      const bZero = !b.avgDurationMs || b.avgDurationMs < ZERO_THRESHOLD ? 1 : 0;
                      if (aZero !== bZero) return aZero - bZero; // zero 는 뒤로
                      return 0; // 같은 그룹 내에서는 기존 순서 유지(서버 정렬 결과)
                    });
                    return list.map((row, i) => {
                      const isZero = !row.avgDurationMs || row.avgDurationMs < ZERO_THRESHOLD;
                      return (
                        <tr key={i} style={{borderBottom: '1px solid #f1f3f5', opacity: isZero ? 0.55 : 1}}>
                          <td style={{padding: '10px', textAlign: 'center', color: '#868e96'}}>{i + 1}</td>
                          <td style={{padding: '10px', fontWeight: '600'}}>{row.pageLabel || '-'}</td>
                          <td style={{padding: '10px', color: '#868e96', fontFamily: 'monospace', fontSize: '12px'}}>{row.path}</td>
                          <td style={{padding: '10px', textAlign: 'center', color: '#868e96'}}>{row.mainMenu || '-'}</td>
                          <td style={{padding: '10px', textAlign: 'right', fontWeight: '600', color: '#5a6af0'}}>{this.formatNumber(row.pv)}</td>
                          <td style={{padding: '10px', textAlign: 'right', color: '#10b981'}}>{this.formatNumber(row.uvUsers)}</td>
                          <td style={{padding: '10px', textAlign: 'right', color: '#f59e0b'}}>{this.formatNumber(row.uvStores)}</td>
                          <td style={{padding: '10px', textAlign: 'right', color: isZero ? '#aaa' : '#8b5cf6'}}>
                            {isZero ? '-' : this.formatDuration(row.avgDurationMs)}
                          </td>
                        </tr>
                      );
                    });
                  })()
                ) : (
                  <tr>
                    <td colSpan="8" style={{padding: '40px', textAlign: 'center', color: '#aaa'}}>
                      {this.isLoading ? '데이터 로딩 중...' : '데이터가 없습니다.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>

        {/* === 기능 사용 분석 (리뷰 관리) === */}
        {(() => {
          const fu = this.featureUsage || {};
          const ad = fu.adoption || {};
          const fEvents = (fu.events || []).slice().sort((a, b) => (b.count || 0) - (a.count || 0));
          const totalStores = fu.totalActiveStores || 0;
          return (
            <>
              {/* 채택률 카드 */}
              <div style={{marginTop: '24px', marginBottom: '8px', fontSize: '15px', fontWeight: '700', color: '#333'}}>
                기능 사용 분석 <span style={{fontSize: '12px', color: '#868e96', fontWeight: '400'}}>· 리뷰 관리(reviewList) — 활성 셀러 {this.formatNumber(totalStores)}개</span>
              </div>
              <Row>
                {this.renderAdoptionCard('AI 답글 채택률', ad.aiCommentRate, `답글 작성 셀러 ${this.formatNumber(ad.aiCommentDenom || 0)}개 중`, '#10b981')}
                {this.renderAdoptionCard('리뷰 동기화 사용률', ad.syncRate, `활성 셀러 ${this.formatNumber(totalStores)}개 중`, '#5a6af0')}
                {this.renderAdoptionCard('CSV 내보내기 사용률', ad.csvRate, `활성 셀러 ${this.formatNumber(totalStores)}개 중`, '#f59e0b')}
              </Row>

              {/* 기능별 사용 순위 */}
              <Card className="mt-1">
                <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #f0f0f0'}}>
                  <span style={{fontSize: '14px', fontWeight: '600'}}>기능별 사용 순위 (리뷰 관리)</span>
                </CardHeader>
                <CardBody style={{padding: '0'}}>
                  <table style={{width: '100%', fontSize: '13px'}}>
                    <colgroup>
                      <col width="6%" /><col /><col width="16%" /><col width="16%" /><col width="16%" />
                    </colgroup>
                    <thead>
                      <tr style={{backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef'}}>
                        <th style={{padding: '10px', textAlign: 'center'}}>순위</th>
                        <th style={{padding: '10px', textAlign: 'left'}}>기능</th>
                        <th style={{padding: '10px', textAlign: 'center'}}>카테고리</th>
                        <th style={{padding: '10px', textAlign: 'right'}}>사용 횟수</th>
                        <th style={{padding: '10px', textAlign: 'right'}}>사용 셀러수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fEvents.length > 0 ? fEvents.map((row, i) => {
                        const meta = REVIEW_FEATURE_LABELS[row.eventName] || { label: row.eventName, category: '-' };
                        return (
                          <tr key={row.eventName || i} style={{borderBottom: '1px solid #f1f3f5'}}>
                            <td style={{padding: '10px', textAlign: 'center', color: '#868e96'}}>{i + 1}</td>
                            <td style={{padding: '10px', fontWeight: '600'}}>{meta.label}</td>
                            <td style={{padding: '10px', textAlign: 'center', color: '#868e96'}}>{meta.category}</td>
                            <td style={{padding: '10px', textAlign: 'right', fontWeight: '600', color: '#5a6af0'}}>{this.formatNumber(row.count)}</td>
                            <td style={{padding: '10px', textAlign: 'right', color: '#f59e0b'}}>{this.formatNumber(row.uvStores)}</td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan="5" style={{padding: '40px', textAlign: 'center', color: '#aaa'}}>
                            {this.isLoading ? '데이터 로딩 중...' : '기능 사용 데이터가 없습니다. (액션 추적 배포 후 셀러 사용 데이터가 쌓이면 표시됩니다)'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </>
          );
        })()}

      </div>
    );
  }
}

export default AdminUsageDashboard;
